import type { ReactNode } from 'react';
import type { TypeTokens } from '../tokens';

/**
 * The surface's own small controls. Every one is drawn from rules and type rather than from filled
 * boxes, so nothing on the page reads as a toolbar.
 */

interface PrimaryActionProps {
  label: string;
  busyLabel?: string;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
}

/**
 * The one real button on the surface. It starts work rather than changing a view, which is the only
 * thing that earns a filled control here.
 */
export const PrimaryAction = ({
  label,
  busyLabel,
  busy = false,
  disabled = false,
  onClick,
  testId,
}: PrimaryActionProps) => (
  <button
    type="button"
    disabled={disabled || busy}
    data-testid={testId}
    onClick={onClick}
    className={`shrink-0 rounded-md bg-bg-brand px-4 py-2 text-sm font-medium text-text-neutral-inverted transition-colors hover:bg-bg-brand-hovered disabled:cursor-not-allowed disabled:bg-bg-neutral disabled:text-text-neutral-secondary`}
  >
    {busy ? (busyLabel ?? label) : label}
  </button>
);

interface SecondaryActionProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}

/**
 * The second thing you can do, beside the first. Outlined rather than filled, so a pair of actions
 * says which one the surface expects without disabling or hiding the other.
 */
export const SecondaryAction = ({
  label,
  onClick,
  disabled = false,
  testId,
}: SecondaryActionProps) => (
  <button
    type="button"
    disabled={disabled}
    data-testid={testId}
    onClick={onClick}
    className="shrink-0 rounded-md border border-border-neutral bg-bg-surface-base px-4 py-2 text-sm font-medium text-text-neutral-primary transition-colors hover:bg-bg-neutral-subtle-hovered disabled:cursor-not-allowed disabled:text-text-neutral-secondary"
  >
    {label}
  </button>
);

/**
 * A section header: a mono label, its count, and a rule running to the end of the row. Used for
 * NEEDS ATTENTION and for every block inside a detail view, so the whole surface is divided the
 * same way.
 */
export const SectionLabel = ({
  type,
  label,
  count,
  tone,
  aside,
  testId,
}: {
  type: Pick<TypeTokens, 'section' | 'metaValue'>;
  // A node, not a string: the field-keys overlay wraps the label to name the field behind it.
  label: ReactNode;
  count?: number;
  tone?: string;
  aside?: string;
  testId?: string;
}) => (
  <div data-testid={testId} className="flex items-center gap-3">
    <span className={`${type.section} ${tone ?? ''}`}>{label}</span>
    {count !== undefined ? <span className={type.metaValue}>{count}</span> : null}
    {aside ? <span className="text-xs text-text-neutral-secondary">{aside}</span> : null}
    <span className="h-px flex-1 bg-border-neutral-subtle" aria-hidden="true" />
  </div>
);

/**
 * Metadata under a claim: mono, uppercase, separated by middots. Items are given as nodes so a
 * confidence can carry its own tone without this component knowing what confidence is.
 */
export const MetaLine = ({
  type,
  items,
  testId,
}: {
  type: Pick<TypeTokens, 'meta'>;
  items: ReactNode[];
  testId?: string;
}) => {
  const present = items.filter(Boolean);
  if (present.length === 0) return null;

  return (
    <span
      data-testid={testId}
      className={`flex flex-wrap items-center gap-x-2 gap-y-1 ${type.meta}`}
    >
      {present.map((item, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a positional list of already-rendered nodes, with no identity of its own
        <span key={index} className="flex items-center gap-2">
          {index > 0 ? <span aria-hidden="true">·</span> : null}
          {item}
        </span>
      ))}
    </span>
  );
};
